import { useAuth } from '@clerk/react'
import {
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { useCallback, useMemo, useState } from 'react'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { usePageTitle } from '@/hooks/use-document-title'
import { useProblemsState } from '@/hooks/use-problems-state'
import { isSolvedStatus, type UserProgressStatus } from '@/lib/progress-status'
import { trpc } from '@/lib/trpc'
import type { ListRef } from '@/lib/url-state'
import { useProblemLists } from './problem-list-context'

/** Sort keys a list may be stored with, in `useProblemsState` form. */
export const LIST_DETAIL_SORT_OPTIONS = [
  'custom',
  'difficulty',
  'questionId',
  'status',
]

/** The same keys, labelled for `ProblemsSortPopover`. */
export const LIST_DETAIL_SORT_POPOVER_OPTIONS = [
  { value: 'custom', label: 'Custom' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

export type ListSortBy = 'custom' | 'difficulty' | 'questionId' | 'status'

/** Maps a stored list sort onto the shared `sortProblems` vocabulary. */
const SORT_MAP: Record<
  string,
  'default' | 'difficulty' | 'questionId' | 'status'
> = {
  custom: 'default',
  difficulty: 'difficulty',
  questionId: 'questionId',
  status: 'status',
}

/** Extended problem type that includes list-specific fields. */
export interface DisplayProblem extends BrowseProblem {
  position: number
  solveStatus: UserProgressStatus | null
}

/**
 * The data layer behind a problem-list detail view: the queries, the
 * search/filter/sort pipeline, the reorder/sort/remove mutations, and the
 * modal state. The desktop table and the mobile row list share all of it and
 * differ only in how they render a row.
 */
export function useListDetail(listId: string, storageKey: string) {
  const { isSignedIn } = useAuth()
  const listCtx = useProblemLists()
  const utils = trpc.useUtils()

  const [showAddProblems, setShowAddProblems] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // --- Data fetching ---

  const { data, isLoading, error } = trpc.lists.getListDetail.useQuery({
    listId,
  })

  // Fall back to the list name from the sidebar context (already loaded) so the
  // tab title doesn't flash to the site default during the per-list query gap.
  const listFallback = listCtx.lists.find((l) => l.id === listId)?.name ?? null
  usePageTitle(data?.list?.name ?? listFallback)

  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Build list refs for the hook (exclude current list)
  const userListRefs: ListRef[] = useMemo(
    () =>
      listCtx.lists.map((l) => ({
        id: l.id,
        name: l.name,
        isSystem: l.isSystem,
      })),
    [listCtx.lists],
  )

  const availableLists = useMemo(
    () =>
      [...listCtx.lists]
        .filter((l) => l.id !== listId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((l) => ({ id: l.id, name: l.name })),
    [listCtx.lists, listId],
  )

  // Derive available topics from the list's problems
  const availableTopics = useMemo(
    () => deriveAvailableTopics((data?.problems as BrowseProblem[]) ?? []),
    [data?.problems],
  )

  // External sort from DB query
  const externalSort = useMemo(
    () =>
      data?.list
        ? {
            sortBy: data.list.sortBy,
            sortDir: (data.list.sortDir as 'asc' | 'desc') ?? 'asc',
          }
        : undefined,
    [data?.list],
  )

  // Filter/sort/search state (synced to URL + sessionStorage)
  const problemsState = useProblemsState(
    `${storageKey}-${listId}`,
    userListRefs,
    availableTopics,
    LIST_DETAIL_SORT_OPTIONS,
    externalSort,
  )
  const { filters, searchQuery, matchMode } = problemsState

  // --- Mutations ---

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  )

  const reorderMutation = trpc.lists.reorder.useMutation({
    onMutate: async ({ problemIds }) => {
      await utils.lists.getListDetail.cancel({ listId })
      const prev = utils.lists.getListDetail.getData({ listId })

      if (prev) {
        const reordered = problemIds
          .map((id, i) => {
            const p = prev.problems.find((p) => p.id === id)
            return p ? { ...p, position: i } : null
          })
          .filter((p): p is NonNullable<typeof p> => p !== null)

        utils.lists.getListDetail.setData(
          { listId },
          { ...prev, problems: reordered },
        )
      }

      return { prev }
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        utils.lists.getListDetail.setData({ listId }, context.prev)
      }
    },
    onSettled: () => {
      utils.lists.getListDetail.invalidate({ listId })
    },
  })

  const updateSortMutation = trpc.lists.updateSort.useMutation({
    onSuccess: () => {
      utils.lists.getListDetail.invalidate({ listId })
    },
  })

  const removeMutation = trpc.lists.removeProblem.useMutation({
    onSuccess: () => {
      utils.lists.getListDetail.invalidate({ listId })
      utils.lists.getUserListData.invalidate()
    },
  })

  // --- Normalize problems to DisplayProblem[] ---

  const problems: DisplayProblem[] = useMemo(() => {
    if (!data) return []
    return data.problems.map((p) => ({
      id: p.id,
      slug: p.slug ?? '',
      title: p.title ?? '',
      externalId: p.externalId,
      difficulty: p.difficulty?.name
        ? { name: p.difficulty.name, color: p.difficulty.color ?? null }
        : null,
      topics: p.topics ?? [],
      position: p.position,
      solveStatus: p.solveStatus,
    }))
  }, [data])

  // --- Search / Filter / Sort pipeline ---

  const fuse = useMemo(
    () => createProblemsFuse(problems as BrowseProblem[]),
    [problems],
  )

  const searched = useMemo(
    () => searchProblems(problems as BrowseProblem[], fuse, searchQuery),
    [problems, fuse, searchQuery],
  )

  const filtered = useMemo(
    () =>
      applyFilters(
        searched,
        filters,
        matchMode,
        userProgress?.progress,
        listCtx.memberships,
      ),
    [searched, filters, matchMode, userProgress, listCtx.memberships],
  )

  const hasSearch = searchQuery.trim().length > 0
  const hasActiveFilters =
    filters.status.values.length > 0 ||
    filters.difficulty.values.length > 0 ||
    filters.topics.values.length > 0 ||
    filters.lists.values.length > 0

  const isCustomSort = data?.list.sortBy === 'custom'

  const displayProblems: DisplayProblem[] = useMemo(() => {
    // Cast filtered back to DisplayProblem (they still carry the extra fields)
    const filteredDisplay = filtered as DisplayProblem[]

    if (isCustomSort) {
      return [...filteredDisplay].sort((a, b) => a.position - b.position)
    }

    // For non-custom sorts, use the shared sortProblems helper
    const sortBy = data?.list.sortBy ?? 'custom'
    const sortDir = (data?.list.sortDir as 'asc' | 'desc') ?? 'asc'

    return sortProblems(
      filteredDisplay,
      SORT_MAP[sortBy] ?? 'default',
      sortDir,
      userProgress?.progress,
    ) as DisplayProblem[]
  }, [
    filtered,
    isCustomSort,
    data?.list.sortBy,
    data?.list.sortDir,
    userProgress,
  ])

  // Dragging reorders `position`, which only means anything when the rows are
  // in stored order and none are hidden.
  const isDragEnabled = isCustomSort && !hasSearch && !hasActiveFilters

  // --- Derived data ---

  const availableDifficulties = useMemo(
    () => deriveAvailableDifficulties(problems),
    [problems],
  )

  const solvedCount = useMemo(() => {
    if (!userProgress?.progress) return 0
    return problems.filter((p) => isSolvedStatus(userProgress.progress[p.id]))
      .length
  }, [problems, userProgress])

  const progressProblems = useMemo(
    () =>
      problems.map((p) => ({
        id: p.id,
        difficulty: p.difficulty?.name ? { name: p.difficulty.name } : null,
      })),
    [problems],
  )

  const progressRecord = useMemo(() => {
    const record: Record<string, UserProgressStatus> = {}
    for (const p of problems) {
      if (p.solveStatus) {
        record[p.id] = p.solveStatus
      }
    }
    return record
  }, [problems])

  // --- Handlers ---

  const handleRemove = useCallback(
    (problemId: string) => {
      removeMutation.mutate({ listId, problemId })
    },
    [removeMutation, listId],
  )

  const handleMoveToTop = useCallback(
    (problemId: string) => {
      const current = displayProblems.map((p) => p.id)
      const idx = current.indexOf(problemId)
      if (idx <= 0) return
      const reordered = [problemId, ...current.filter((id) => id !== problemId)]
      reorderMutation.mutate({ listId, problemIds: reordered })
    },
    [displayProblems, reorderMutation, listId],
  )

  const handleMoveToBottom = useCallback(
    (problemId: string) => {
      const current = displayProblems.map((p) => p.id)
      const idx = current.indexOf(problemId)
      if (idx === -1 || idx === current.length - 1) return
      const reordered = [...current.filter((id) => id !== problemId), problemId]
      reorderMutation.mutate({ listId, problemIds: reordered })
    },
    [displayProblems, reorderMutation, listId],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = displayProblems.findIndex((p) => p.id === active.id)
      const newIndex = displayProblems.findIndex((p) => p.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const reordered = arrayMove(displayProblems, oldIndex, newIndex)

      reorderMutation.mutate({
        listId,
        problemIds: reordered.map((p) => p.id),
      })
    },
    [displayProblems, reorderMutation, listId],
  )

  const handleSortChange = useCallback(
    (newSortBy: string, newSortDir: 'asc' | 'desc') => {
      updateSortMutation.mutate({
        listId,
        sortBy: newSortBy as ListSortBy,
        sortDir: newSortDir,
      })
    },
    [updateSortMutation, listId],
  )

  // Adding problems happens inside the modal; refetch once it closes.
  const handleAddProblemsOpenChange = useCallback(
    (open: boolean) => {
      setShowAddProblems(open)
      if (!open) {
        utils.lists.getListDetail.invalidate({ listId })
      }
    },
    [utils, listId],
  )

  return {
    // Query state
    data,
    isLoading,
    error,
    isSignedIn,
    userProgress,
    // Problems
    problems,
    displayProblems,
    solvedCount,
    progressProblems,
    progressRecord,
    // Filter/sort/search state
    ...problemsState,
    availableTopics,
    availableDifficulties,
    availableLists,
    hasSearch,
    hasActiveFilters,
    isCustomSort,
    isDragEnabled,
    // Drag and drop
    sensors,
    handleDragEnd,
    // Handlers
    handleRemove,
    handleMoveToTop,
    handleMoveToBottom,
    handleSortChange,
    // Modal + banner state
    showAddProblems,
    setShowAddProblems,
    handleAddProblemsOpenChange,
    showEdit,
    setShowEdit,
    showDelete,
    setShowDelete,
    dismissed,
    setDismissed,
  }
}
